import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { THEMES, LAYOUTS, DEFAULT_THEME, DEFAULT_LAYOUT } from "./themes";

const ThemeContext = createContext(null);

const THEME_KEY = "rc_theme";
const LAYOUT_KEY = "rc_layout";

function applyTheme(themeId) {
    const theme = THEMES[themeId] || THEMES[DEFAULT_THEME];
    const root = document.documentElement;
    Object.entries(theme.vars).forEach(([k, v]) => {
        root.style.setProperty(`--${k}`, v);
    });
    // apply light/dark tag for body-level styling
    root.dataset.themeMode = theme.mode;
    root.dataset.theme = theme.id;
    // hero glow variable
    root.style.setProperty("--hero-glow", theme.heroGlow);
    return theme;
}

export function ThemeProvider({ children }) {
    const [themeId, setThemeIdState] = useState(() => localStorage.getItem(THEME_KEY) || DEFAULT_THEME);
    const [layoutId, setLayoutIdState] = useState(() => localStorage.getItem(LAYOUT_KEY) || DEFAULT_LAYOUT);

    useEffect(() => {
        applyTheme(themeId);
    }, [themeId]);

    const setTheme = useCallback((id) => {
        if (!THEMES[id]) return;
        localStorage.setItem(THEME_KEY, id);
        setThemeIdState(id);
    }, []);

    const setLayout = useCallback((id) => {
        if (!LAYOUTS[id]) return;
        localStorage.setItem(LAYOUT_KEY, id);
        setLayoutIdState(id);
    }, []);

    const value = useMemo(() => ({
        themeId,
        theme: THEMES[themeId] || THEMES[DEFAULT_THEME],
        setTheme,
        layoutId,
        layout: LAYOUTS[layoutId] || LAYOUTS[DEFAULT_LAYOUT],
        setLayout,
        themes: THEMES,
        layouts: LAYOUTS,
    }), [themeId, layoutId, setTheme, setLayout]);

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
    const ctx = useContext(ThemeContext);
    if (!ctx) throw new Error("useTheme must be inside ThemeProvider");
    return ctx;
}
